import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useInsumos,
  useCrearInsumo,
  useActualizarInsumo,
  useEliminarInsumo
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm, estadoColumn } from '../../components/admin/crud';
import { formatMoneda } from '../../utils/numberUtils';
import {
  codigoField,
  descripcionField,
  estadoField,
  unidadMedidaField,
  categoriaField,
  precioField
} from '../../components/admin/crud/CrudForm';

import type { Insumo } from '../../types';
import type { CrearInsumo } from '../../services/referenciasService';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const InsumosView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // ✅ PERMISOS UAU - Control granular de botones
  const canCreate = hasPermission('insumos.crear');
  const canEdit = hasPermission('insumos.editar');
  const canDelete = hasPermission('insumos.eliminar');

  // Estado local
  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Insumo;
  }>({
    isOpen: false,
    mode: 'create',
  });

  // Hooks de datos
  const { data, isLoading, error } = useInsumos(filtros);
  const crearMutation = useCrearInsumo();
  const actualizarMutation = useActualizarInsumo();
  const eliminarMutation = useEliminarInsumo();

  // ===============================
  // CONFIGURACIÓN DE COLUMNAS
  // ===============================

  const columns = useMemo(() => [
    {
      key: 'codigo',
      title: t('common.code'),
      width: '120px',
    },
    {
      key: 'descripcion',
      title: t('common.description'),
      width: '200px',
    },
    {
      key: 'unidad_medida',
      title: t('common.unit'),
      width: '100px',
    },
    {
      key: 'categoria',
      title: t('common.category'),
      width: '130px',
      render: (categoria: string) => categoria || '-',
    },
    {
      key: 'precio_unitario',
      title: t('common.price'),
      width: '120px',
      render: (precio: number | null) => formatMoneda(precio),
    },
    estadoColumn<Insumo>(),
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FILTROS
  // ===============================

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('admin.search_placeholder'),
      type: 'text' as const,
      placeholder: t('admin.search_placeholder'),
    },
    {
      key: 'unidad_medida',
      label: t('common.unit_of_measure'),
      type: 'select' as const,
      options: [
        { value: 'litros', label: t('units.liters') },
        { value: 'kg', label: t('units.kilograms') },
        { value: 'ton', label: t('units.tons') },
        { value: 'unidad', label: t('units.unit') },
        { value: 'metro', label: t('units.meter') },
        { value: 'm2', label: t('units.square_meter') },
        { value: 'm3', label: t('units.cubic_meter') },
      ],
    },
    {
      key: 'categoria',
      label: t('common.category'),
      type: 'text' as const,
      placeholder: t('admin.filter_by_category'),
    },
  ], [t]);

  // ===============================
  // MANEJADORES DE EVENTOS
  // ===============================

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    setFormModal({
      isOpen: true,
      mode: 'create',
    });
  };

  const handleEdit = (insumo: Insumo) => {
    setFormModal({
      isOpen: true,
      mode: 'edit',
      data: insumo,
    });
  };

  const handleDelete = async (insumo: Insumo) => {
    try {
      await eliminarMutation.mutateAsync(insumo.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando insumo:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearInsumo) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({
          id: formModal.data.id,
          datos,
        });
        toast.success(t('admin.update_success'));
      }

      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario insumo:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  // ===============================
  // ESTADO DE CARGA DEL FORMULARIO
  // ===============================

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button
          onClick={() => navigate('/admin')}
          className="hover:text-gray-700 transition-colors"
        >
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.supplies')}</span>
      </div>

      {/* Botón volver */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      {/* Tabla CRUD */}
      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.supplies_title')}
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Modal de formulario */}
      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={[codigoField, descripcionField, unidadMedidaField, categoriaField, precioField, estadoField]}
        title={formModal.mode === 'create' ? t('admin.create_supply') : t('admin.edit_item')}
        submitText={formModal.mode === 'create' ? t('common.save') : t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default InsumosView;
